import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { refreshApiView } from '@e3dc-react/shell/State/ApiView/ApiViewActions'
import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { DownloadsParent, UpdateFileFormValues, UploadFileFormValues } from '../../models'
import { uploadFile } from '../../api'
import UploadForm from './UploadForm'
import { useUploadFileFormInitializer } from '../../hooks'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { DownloadItemAccess } from '../../dto'
import { closeSnackbar } from 'notistack'

export interface UploadFileDialogProps {
  parent: DownloadsParent
  onClose?: () => void
  closeEditModal?: () => void
}

export type UploadFileForm = Omit<UpdateFileFormValues, 'parentId'>

/**
 * Dialog to upload files to the documents feature
 */
const UploadFileDialog: React.FC<UploadFileDialogProps> = ({ parent, closeEditModal, onClose }) => {
  const { t } = useCustomTranslation()
  const { enqueueErrorStack, enqueueSuccessStack, enqueueLoadingStack } = useSnackbar()
  const authFetch = useAuthFetchWithResult()
  const dispatch = useDispatch()
  const [file, setFile] = useState<File | undefined>(undefined)
  const { initialValues, validationSchema } = useUploadFileFormInitializer({ parent, isUpload: true })

  const methods = useForm<UploadFileFormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(validationSchema),
    mode: 'onChange',
  })

  const onUploadFile = async (values: UploadFileFormValues): Promise<void> => {
    if (!file) return
    /* disabled eslint and ts for formdata to avoid unessesary .toString calls */
    /* eslint-disable @typescript-eslint/ban-ts-comment*/
    const formData = new FormData()
    formData.append('file', file)
    formData.append('fileName', values.fileName)
    formData.append('language', values.language)
    formData.append('access', values.isPublic ? DownloadItemAccess.PUBLIC : DownloadItemAccess.PORTAL)
    /* @ts-ignore */
    if (values.urlName || values.urlName !== '') formData.append('urlName', values.urlName)
    formData.append('countryList', JSON.stringify(values.countryList))
    /* @ts-ignore */
    formData.append('path', parent.path)
    /* @ts-ignore */
    formData.append('visibleToCustomer', values.visibleToCustomer)
    /* @ts-ignore */
    formData.append('visibleToInstaller', values.visibleToInstaller)
    /* eslint-enable @typescript-eslint/ban-ts-comment*/
    const loadingSnackbarKey = enqueueLoadingStack(t('documents.upload.loading'))
    uploadFile(formData, authFetch)
      .then(() => enqueueSuccessStack(t('documents.upload.success')))
      .catch(error => {
        enqueueErrorStack(t('documents.upload.error'))
        console.error('Error uploading file:', error)
      })
      .finally(() => {
        closeSnackbar(loadingSnackbarKey)
        dispatch(refreshApiView())
      })
    onClose?.()
    closeEditModal?.()
  }

  return (
    <FormProvider {...methods}>
      <Dialog
        maxWidth="xs"
        title={t('documents.upload.form.title')}
        onClose={onClose}
        actions={{
          primary: {
            name: t('documents.upload.button'),
            onClick: methods.handleSubmit(onUploadFile),
            disabled: !methods.formState.isValid && methods.formState.submitCount > 0,
          },
          secondary: {
            name: t('common.cancel'),
            onClick: () => onClose?.(),
          },
        }}
      >
        <form onSubmit={methods.handleSubmit(onUploadFile)}>
          <UploadForm parent={parent} setUploadedFile={setFile} />
        </form>
      </Dialog>
    </FormProvider>
  )
}

export default UploadFileDialog
